// Single definition of the POST body for /api/employees/:id/enrollments.
// Previously each surface (modify-modal enroll, modify-modal waiver, and the
// EnrollmentWizard) hand-built this object separately, so the shape drifted and
// every field change had to be made in three places. This is the one place that
// defines it. Pure + snapshot-tested so the server contract can't change by
// accident.

export interface BuildEnrollmentPayloadInput {
  // Always present
  employeeId: number;
  planId: number;
  clientId: number;
  coverageTier: string;
  enrollmentSource: string; // "new_hire" | "open_enrollment" | "qualifying_event"
  dependentIds?: number[]; // defaults to []

  // Optional — included in the payload ONLY when not `undefined` (pass `null`
  // to include an explicit null, pass `undefined` to omit the key entirely).
  effectiveDate?: string | null;
  signatureId?: number | string | null;
  waivedCoverage?: boolean;
  waiverReason?: string | null;
  status?: string;
  annualElection?: string; // FSA
  coverageAmount?: string | number; // life / disability
  beneficiaryName?: string | null;
  beneficiaryRelationship?: string | null;
  dependentWaiverReasons?: Record<string, string> | null;
}

const OPTIONAL_KEYS: (keyof BuildEnrollmentPayloadInput)[] = [
  "effectiveDate",
  "signatureId",
  "waivedCoverage",
  "waiverReason",
  "status",
  "annualElection",
  "coverageAmount",
  "beneficiaryName",
  "beneficiaryRelationship",
  "dependentWaiverReasons",
];

export function buildEnrollmentPayload(
  input: BuildEnrollmentPayloadInput,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    employeeId: input.employeeId,
    planId: input.planId,
    clientId: input.clientId,
    coverageTier: input.coverageTier,
    dependentIds: input.dependentIds ?? [],
    enrollmentSource: input.enrollmentSource,
  };
  for (const key of OPTIONAL_KEYS) {
    if (input[key] !== undefined) payload[key] = input[key];
  }
  return payload;
}
